import styled from 'foundations-theming/utils';
import useDebouncedEditorStateContext from 'hubspot-prosemirror/prosemirror/contexts/useDebouncedEditorStateContext';
import { Popover } from 'foundations-components/transitional/components/Popover';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { useEffect, useRef, useState, useContext } from 'react';
import ContentAssistantInlineAutocompleteMenu from './CAAutocompleteMenu';
import { initContentAssistantSlashPluginState } from './utils';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import { jsx as _jsx } from "react/jsx-runtime";
const OptionsUl = styled.ul.withConfig({
  displayName: "CADropdown__OptionsUl"
})(["-webkit-tap-highlight-color:rgba(0,0,0,0);border-radius:inherit;list-style:none;margin:", ";max-height:220px;overflow-x:hidden;overflow-y:auto;padding:", ";position:relative;"], ({
  theme
}) => theme.space['0'], ({
  theme
}) => theme.space['0']);
const OptionsLi = styled.li.withConfig({
  displayName: "CADropdown__OptionsLi"
})(["padding:", ";&.focus{background-color:", ";}"], ({
  theme
}) => `${theme.space['200']} ${theme.space['300']}`, ({
  theme
}) => theme.color['fill-field-hover']);
const OptionButton = styled.button.withConfig({
  displayName: "CADropdown__OptionButton"
})(["background:none;border:", ";color:inherit;padding:", ";text-align:inherit;text-transform:inherit;"], ({
  theme
}) => theme.borderWidth['0'], ({
  theme
}) => theme.space['0']);
export default function ContentAssistantDropdown({
  view,
  options,
  pluginKey,
  onInsert,
  getSelectedCommandParams,
  usageDescription
}) {
  const {
    editorState
  } = useDebouncedEditorStateContext();
  const pluginState = pluginKey.getState(editorState) || {};
  const {
    searchTerm,
    currentDropdownIndex,
    popoverActive
  } = pluginState;
  const displayOptions = searchTerm ? Object.keys(options).filter(key => {
    return I18n.text(options[key].labelKey).toLowerCase().indexOf(searchTerm.toLowerCase()) > -1;
  }) : Object.keys(options);
  const {
    CONTENT_ASSISTANT_SLASH_COMMAND: {
      onSelectContentAssistantOption = undefined,
      trackOpenPopover = undefined
    } = {}
  } = useContext(EditorConfigContext);
  const hasTrackedOpen = useRef(false);
  useEffect(() => {
    if (trackOpenPopover && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      trackOpenPopover();
    }
  }, [trackOpenPopover]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const focusedIndex = activeIndex !== null ? activeIndex : currentDropdownIndex;
  useEffect(() => {
    if (currentDropdownIndex !== undefined && currentDropdownIndex >= 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveIndex(null);
    }
  }, [currentDropdownIndex]);

  // since the browser focus is still on the editor so user can continue to type
  // have to listen to on Enter event while dropdown is open
  useEffect(() => {
    const keyupListener = e => {
      if (e.key === 'Enter') {
        if (currentDropdownIndex < 0 || currentDropdownIndex >= displayOptions.length) {
          return;
        }
        const optionId = displayOptions[currentDropdownIndex];
        if (onSelectContentAssistantOption) {
          onSelectContentAssistantOption({
            selected: optionId,
            command: options[optionId].id
          });
        }
        return setSelectedOption(options[optionId]);
      }
    };
    window.addEventListener('keyup', keyupListener);
    return () => {
      window.removeEventListener('keyup', keyupListener);
    };
  }, [currentDropdownIndex, selectedOption, options, displayOptions, onSelectContentAssistantOption]);
  const onSelectCommand = selectedCommand => {
    if (onSelectContentAssistantOption && selectedCommand.id) {
      onSelectContentAssistantOption({
        selected: selectedCommand.id,
        command: selectedCommand.id
      });
    }
    view.dispatch(view.state.tr.setMeta(pluginKey, Object.assign({}, pluginState, {
      popoverActive: true
    })).setMeta('addToHistory', false));
    return setSelectedOption(selectedCommand);
  };
  const handleOnMenuClose = () => {
    view.dispatch(view.state.tr.setMeta(pluginKey, initContentAssistantSlashPluginState()).setMeta('addToHistory', false));
  };
  const dropdownContent =
  /*#__PURE__*/
  // @bend-expect trellis-tokenized
  _jsx(OptionsUl, {
    "data-test-id": "content-assistant-slash-dropdown-list",
    children: displayOptions.length ? displayOptions.map((key, index) => {
      const optionButton =
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(OptionButton, {
        className: 'truncate-text text-center',
        onClick: onSelectCommand.bind(null, options[key]),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: options[key].labelKey
        })
      });
      return (
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        _jsx(OptionsLi, {
          "data-test-id": `content-assistant-slash-option`,
          className: focusedIndex === index ? "focus" : "",
          onMouseEnter: () => setActiveIndex(index),
          onMouseLeave: () => setActiveIndex(null),
          children: optionButton
        }, key)
      );
    }) :
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(OptionsLi, {
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: 'inlineAutocompleteMenu.noResult'
      })
    })
  });
  const showPopover = popoverActive && selectedOption;
  if (showPopover) {
    return /*#__PURE__*/_jsx(ContentAssistantInlineAutocompleteMenu, {
      onInsert: onInsert,
      onClose: handleOnMenuClose,
      selectedItem: selectedOption,
      view: view,
      state: pluginState,
      getSelectedCommandParams: getSelectedCommandParams,
      trackInteraction: action => {
        if (onSelectContentAssistantOption) {
          const optionId = displayOptions[currentDropdownIndex];
          onSelectContentAssistantOption({
            selected: action,
            command: options[optionId].id
          });
        }
      },
      usageDescription: usageDescription,
      pluginKey: pluginKey
    });
  }
  return /*#__PURE__*/_jsx(Popover, {
    autoPlacement: true,
    content: dropdownContent,
    open: true,
    placement: "bottom",
    "data-test-id": 'content-assistant-slash-popover',
    children: /*#__PURE__*/_jsx("span", {})
  });
}